import { useCallback, useEffect, useRef, type RefObject } from "react";
import type { TimelineTurn } from "@/hooks/use-conversation-navigation";
import type { ConversationTarget } from "@/lib/conversation-target";

/** Reads mounted Virtua rows only. It never positions the transcript. */
export function useConversationScrollSpy({
  rootRef,
  enabled,
  turns,
  navigationRequestId,
  onVisibleTargetChange,
}: {
  rootRef: RefObject<HTMLDivElement | null>;
  enabled: boolean;
  turns: TimelineTurn[];
  navigationRequestId: number;
  onVisibleTargetChange: (target: ConversationTarget) => void;
}) {
  const armed = useRef(false);
  const lastTarget = useRef<string | null>(null);

  useEffect(() => {
    // A new hash/popstate request owns positioning until the next transcript gesture.
    armed.current = false;
    lastTarget.current = null;
  }, [navigationRequestId]);

  const arm = useCallback((event: Event) => {
    if (event.type === "wheel" || event.type === "touchmove" || event.type === "pointerdown"
      || event.type === "keydown") armed.current = true;
  }, []);

  useEffect(() => {
    const scroller = rootRef.current?.querySelector<HTMLElement>('[role="log"]');
    if (!enabled || !scroller || typeof IntersectionObserver === "undefined") return;

    const messageTargets = new Map<string, ConversationTarget>();
    const headingTargets = new Map<string, ConversationTarget>();
    // The transcript and rail are chronological, regardless of pagination order.
    for (const turn of turns) {
      messageTargets.set(turn.startMessageId, turn.primaryTarget);
      for (const reply of turn.replies) {
        messageTargets.set(reply.messageId, { kind: "message", messageId: reply.messageId });
      }
      for (const heading of turn.headings) {
        headingTargets.set(`${heading.message_id}:${heading.key}`, {
          kind: "message", messageId: heading.message_id, headingKey: heading.key,
        });
      }
    }

    const observed = new Map<HTMLElement, ConversationTarget>();
    const evaluate = () => {
      if (!armed.current) return;
      const viewport = scroller.getBoundingClientRect();
      const readingLine = viewport.top + Math.min(96, viewport.height * 0.25);
      let before: { top: number; target: ConversationTarget } | null = null;
      let after: { top: number; target: ConversationTarget } | null = null;
      for (const [element, target] of observed) {
        const rect = element.getBoundingClientRect();
        const row = element.hasAttribute("data-conversation-message-anchor")
          ? element : element.closest<HTMLElement>("[data-conversation-message-anchor]");
        const rowRect = row?.getBoundingClientRect();
        if (rect.height === 0 || !rowRect || rowRect.bottom <= viewport.top || rowRect.top >= viewport.bottom) continue;
        if (rect.top <= readingLine) {
          if (!before || rect.top > before.top) before = { top: rect.top, target };
        } else if (!after || rect.top < after.top) {
          after = { top: rect.top, target };
        }
      }
      const target = before?.target ?? after?.target;
      if (!target) return;
      const key = JSON.stringify(target);
      if (key === lastTarget.current) return;
      lastTarget.current = key;
      onVisibleTargetChange(target);
    };
    const observer = new IntersectionObserver(evaluate, { root: scroller, threshold: 0 });
    const sync = () => {
      observer.disconnect();
      observed.clear();
      for (const row of scroller.querySelectorAll<HTMLElement>("[data-conversation-message-anchor]")) {
        const messageId = row.dataset.messageId;
        const target = messageId ? messageTargets.get(messageId) : undefined;
        if (target) {
          observed.set(row, target);
          observer.observe(row);
        }
      }
      for (const heading of scroller.querySelectorAll<HTMLElement>("[data-heading-key]")) {
        const messageId = heading.closest<HTMLElement>("[data-conversation-message-anchor]")?.dataset.messageId;
        const key = heading.dataset.headingKey;
        const target = messageId && key ? headingTargets.get(`${messageId}:${key}`) : undefined;
        if (target) {
          observed.set(heading, target);
          observer.observe(heading);
        }
      }
      evaluate();
    };
    const mutations = new MutationObserver(sync);
    mutations.observe(scroller, { childList: true, subtree: true, attributes: true, attributeFilter: ["data-heading-key"] });
    scroller.addEventListener("scroll", evaluate, { passive: true });
    sync();
    return () => {
      mutations.disconnect();
      observer.disconnect();
      scroller.removeEventListener("scroll", evaluate);
    };
  }, [enabled, onVisibleTargetChange, rootRef, turns]);

  return arm;
}
